/**
 * @fileoverview Optimization Queue Hook (Refactored)
 * 
 * Composed hook that combines all optimization queue functionality.
 * This is the main entry point - it orchestrates the smaller, focused hooks.
 * 
 * Architecture:
 * - useJobState: State management
 * - useJobActions: API calls
 * - useSSEConnection: Real-time connection
 * - useJobEventHandler: Event parsing
 * - useOptimizationPolling: Smart polling
 * - useJobCleanup: Memory management
 */

import { useEffect, useCallback } from 'react';
import { notifyCreditChange } from '../useCredits';
import { useJobState } from './useJobState';
import { useJobActions } from './useJobActions';
import { useSSEConnection } from './useSSEConnection';
import { useJobEventHandler } from './useJobEventHandler';
import { useOptimizationPolling } from './useOptimizationPolling';
import { useJobCleanup } from './useJobCleanup';
import type { UseOptimizationQueueOptions, OptimizationJob, ApiTypes } from './types';
import { optimizationDebugLog } from '../../utils/optimizationDebugLogger';

export type { UseOptimizationQueueOptions } from './types';
export type { OptimizationQueueState } from './types';

/**
 * Main hook for managing the optimization queue
 * 
 * @example
 * ```tsx
 * const {
 *   activeJobs,
 *   completedJobs,
 *   startOptimization,
 *   cancelOptimization,
 * } = useOptimizationQueue({ autoConnect: true });
 * ```
 */
export function useOptimizationQueue(options: UseOptimizationQueueOptions = {}) {
  const { autoConnect = false, pollInterval = 3000 } = options;

  // Core state management
  const {
    state,
    updateJobsFromFetch,
    addJob,
    updateJob,
    removeJobFromState,
    markJobCompleted,
    markJobFailed,
    setLoading,
    setError,
    setConnected,
    setShouldPoll,
    selectJob,
    resetState,
  } = useJobState();

  // API actions
  const {
    fetchJobs: fetchJobsFromAPI,
    startOptimization: startOptimizationAPI,
    cancelOptimization: cancelOptimizationAPI,
    removeJob: removeJobAPI,
    storeCompletedJob,
    getHistoricalJobs,
  } = useJobActions();

  // SSE connection
  const {
    connected,
    connect,
    disconnect,
    isConnected,
  } = useSSEConnection({
    onConnectionChange: setConnected,
  });

  // Wrapped fetch that updates state
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { jobsToKeep, jobsToStore } = await fetchJobsFromAPI();
      
      // Update state with filtered jobs
      updateJobsFromFetch(jobsToKeep);

      // Store completed jobs asynchronously
      jobsToStore.forEach((job) => {
        storeCompletedJob(job);
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isTimeout = errorMessage.toLowerCase().includes('timeout');
      
      setError(
        isTimeout
          ? 'Job status check timed out - optimization may still be running'
          : 'Failed to fetch optimization jobs'
      );
    } finally {
      setLoading(false);
    }
  }, [fetchJobsFromAPI, updateJobsFromFetch, storeCompletedJob, setLoading, setError]);

  // Delayed fetch after completion
  const scheduleFetch = useCallback(() => {
    setTimeout(fetchJobs, 300);
  }, [fetchJobs]);

  // Event handler callbacks
  const onProgress = useCallback((
    jobId: string,
    progress: number,
    current: number,
    total: number,
    estimatedTimeRemaining?: number
  ) => {
    updateJob(jobId, {
      progress,
      current,
      total,
      estimatedTimeRemaining,
    });
  }, [updateJob]);

  const onCompleted = useCallback((jobId: string, resultData: Partial<OptimizationJob>) => {
    optimizationDebugLog('useOptimizationQueue', 'Job completed', {
      jobId,
      hasResultData: !!resultData,
      hasBestResult: !!resultData.bestResult,
    }, { console: false });
    
    markJobCompleted(jobId, resultData);
    notifyCreditChange();
  }, [markJobCompleted]);

  const onFailed = useCallback((jobId: string, error: string) => {
    markJobFailed(jobId, error);
  }, [markJobFailed]);

  // Event handler
  const { subscribeToJob, subscribeGlobal } = useJobEventHandler({
    onProgress,
    onCompleted,
    onFailed,
    onShouldFetch: scheduleFetch,
  });

  // Polling
  const hasActiveJobs = state.activeJobs.length > 0 || state.queuedJobs.length > 0;
  
  useOptimizationPolling({
    pollInterval,
    shouldPoll: state.shouldPoll,
    hasActiveJobs,
    onPoll: fetchJobs,
  });

  // Memory cleanup
  useJobCleanup({
    jobs: state.jobs,
    onCleanup: updateJobsFromFetch,
  });

  // Public actions
  const startOptimization = useCallback(async (
    request: ApiTypes.OptimizationRequest
  ): Promise<string> => {
    const { optimizationId, newJob } = await startOptimizationAPI(request);
    
    addJob(newJob);
    
    // Ensure SSE is connected
    if (!isConnected()) {
      connect();
    }
    
    // Refresh from server
    setTimeout(fetchJobs, 100);
    
    return optimizationId;
  }, [startOptimizationAPI, addJob, isConnected, connect, fetchJobs]);

  const cancelOptimization = useCallback(async (jobId: string) => {
    await cancelOptimizationAPI(jobId);
    removeJobFromState(jobId);
  }, [cancelOptimizationAPI, removeJobFromState]);

  const removeJob = useCallback(async (jobId: string) => {
    await removeJobAPI(jobId);
    removeJobFromState(jobId);
  }, [removeJobAPI, removeJobFromState]);

  const startPolling = useCallback(() => {
    setShouldPoll(true);
  }, [setShouldPoll]);

  const stopPolling = useCallback(() => {
    setShouldPoll(false);
  }, [setShouldPoll]);

  // Initial setup
  useEffect(() => {
    fetchJobs();

    if (autoConnect || hasActiveJobs) {
      connect();
    }
  }, [autoConnect]); // Only run on mount and autoConnect change

  // Auto-connect when jobs become active
  useEffect(() => {
    if (hasActiveJobs && !connected) {
      connect();
      setShouldPoll(true);
    } else if (!hasActiveJobs && connected && !autoConnect) {
      // Optionally disconnect when no active jobs
      // disconnect();
    }
  }, [hasActiveJobs, connected, autoConnect, connect, setShouldPoll]);

  // Subscribe to SSE events
  useEffect(() => {
    if (!connected) return;

    const unsubscribers: (() => void)[] = [];

    // Global subscription
    unsubscribers.push(subscribeGlobal());

    // Per-job subscriptions
    state.jobs.forEach((job) => {
      unsubscribers.push(subscribeToJob(job.id));
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [connected, state.jobs, subscribeToJob, subscribeGlobal]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
      resetState();
    };
  }, [disconnect, resetState]);

  return {
    // State
    ...state,
    connected,
    
    // Actions
    fetchJobs,
    startOptimization,
    cancelOptimization,
    removeJob,
    connect,
    disconnect,
    
    // Polling control
    startPolling,
    stopPolling,
    
    // Selection
    selectJob,
    
    // Database access
    getHistoricalJobs,
    
    // Alias
    refresh: fetchJobs,
  };
}
